// ---------------------------------------------------------------------------
// Transferência de estoque entre PDVs — vocabulário do submódulo (handoff 0099)
//
// Enums e rótulos vivem aqui, não espalhados pelas telas. Os valores espelham
// o contrato do backend; os rótulos são pt-BR (labels de UI).
// ---------------------------------------------------------------------------

// Permissões (Master-only na Fase 1 — a distribuição por papel vem depois).
export const TRANSFER_PERMISSIONS = Object.freeze({
   READ: 'inventory:transfer:read',
   DISPATCH: 'inventory:transfer:dispatch',
   RECEIVE: 'inventory:transfer:receive',
   SETTLE: 'inventory:transfer:settle',
   CANCEL: 'inventory:transfer:cancel',
});

// ─── Status da ordem ────────────────────────────────────────────────────────
// OPEN (sem remessa) → PARTIAL (remessa feita, contagem incompleta) → CLOSED
// (contagem 100%, automático) | CANCELLED. Não existe fechar manual: para
// encerrar mandando menos, edita-se o pedido para baixo.
export const ORDER_STATUS = Object.freeze({
   OPEN: 'OPEN',
   PARTIAL: 'PARTIAL',
   CLOSED: 'CLOSED',
   CANCELLED: 'CANCELLED',
});

// Paleta das pílulas (refatoração visual 2026-09-10, UI_SPEC do handoff de
// design): pastel sem fill sólido em superfície clara; `solidBg` é a variante
// OPACA para o header escuro da página, onde pastel não atinge contraste.
export const ORDER_STATUS_CONFIG = Object.freeze({
   [ORDER_STATUS.OPEN]: {
      label: 'Aberta',
      description: 'Pedido criado, nada expedido — o estoque está reservado na origem.',
      bg: 'rgba(64,152,239,0.14)',
      border: 'transparent',
      text: '#0b3a63',
      solidBg: 'rgba(64,152,239,0.92)',
   },
   [ORDER_STATUS.PARTIAL]: {
      label: 'Parcial',
      description: 'Há remessa expedida e a contagem ainda não fechou.',
      bg: 'rgba(245,158,11,0.16)',
      border: 'transparent',
      text: '#8a5a00',
      solidBg: 'rgba(217,119,6,0.92)',
   },
   [ORDER_STATUS.CLOSED]: {
      label: 'Concluída',
      description: 'Contagem satisfeita: pedido = expedido = conferido + tratado.',
      bg: 'rgba(46,125,50,0.14)',
      border: 'transparent',
      text: '#1b5e20',
      solidBg: 'rgba(46,125,50,0.92)',
   },
   [ORDER_STATUS.CANCELLED]: {
      label: 'Cancelada',
      description: 'Cancelada antes de qualquer expedição.',
      bg: 'rgba(0,0,0,0.07)',
      border: 'transparent',
      text: '#555',
      solidBg: 'rgba(90,90,90,0.92)',
   },
});

export const ORDER_STATUS_OPTIONS = Object.freeze([
   { value: ORDER_STATUS.OPEN, label: 'Aberta' },
   { value: ORDER_STATUS.PARTIAL, label: 'Parcial' },
   { value: ORDER_STATUS.CLOSED, label: 'Concluída' },
   { value: ORDER_STATUS.CANCELLED, label: 'Cancelada' },
]);

// ─── Status da remessa ──────────────────────────────────────────────────────
// DISPATCHED (em trânsito) → RECEIVED | RECEIVED_WITH_DIVERGENCE → (tratativa)
// · CANCELLED (cancelamento em trânsito devolve o estoque à origem).
export const SHIPMENT_STATUS = Object.freeze({
   DISPATCHED: 'DISPATCHED',
   RECEIVED: 'RECEIVED',
   RECEIVED_WITH_DIVERGENCE: 'RECEIVED_WITH_DIVERGENCE',
   CANCELLED: 'CANCELLED',
});

export const SHIPMENT_STATUS_CONFIG = Object.freeze({
   [SHIPMENT_STATUS.DISPATCHED]: {
      label: 'Em trânsito',
      description: 'Saiu da origem e ainda não foi conferida no destino.',
      bg: 'rgba(64,152,239,0.14)',
      border: 'transparent',
      text: '#0b3a63',
      solidBg: 'rgba(64,152,239,0.92)',
   },
   [SHIPMENT_STATUS.RECEIVED]: {
      label: 'Conferida',
      description: 'Conferida no destino sem divergência.',
      bg: 'rgba(46,125,50,0.14)',
      border: 'transparent',
      text: '#1b5e20',
      solidBg: 'rgba(46,125,50,0.92)',
   },
   [SHIPMENT_STATUS.RECEIVED_WITH_DIVERGENCE]: {
      label: 'Divergência',
      description: 'Conferida com falta — o gestor precisa registrar a tratativa.',
      bg: 'rgba(220,38,38,0.12)',
      border: 'transparent',
      text: '#a01b1b',
      solidBg: 'rgba(220,38,38,0.92)',
   },
   [SHIPMENT_STATUS.CANCELLED]: {
      label: 'Cancelada',
      description: 'Cancelada em trânsito — o estoque voltou à origem.',
      bg: 'rgba(0,0,0,0.07)',
      border: 'transparent',
      text: '#555',
      solidBg: 'rgba(90,90,90,0.92)',
   },
});

// `ALL` é valor EXPLÍCITO do contrato (adendo 0099 §4.3 — 2026-08-21), não
// ausência de filtro: omitir `status` continua significando DISPATCHED. Por
// isso a tela sempre envia o param — "Todos" só existe porque o backend passou
// a entendê-lo, e não como sinônimo de "não filtrar".
export const SHIPMENT_STATUS_ALL = 'ALL';

export const SHIPMENT_STATUS_OPTIONS = Object.freeze([
   { value: SHIPMENT_STATUS.DISPATCHED, label: 'Em trânsito' },
   { value: SHIPMENT_STATUS.RECEIVED, label: 'Conferida' },
   {
      value: SHIPMENT_STATUS.RECEIVED_WITH_DIVERGENCE,
      label: 'Conferida com divergência',
   },
   { value: SHIPMENT_STATUS.CANCELLED, label: 'Cancelada' },
   { value: SHIPMENT_STATUS_ALL, label: 'Todas as situações' },
]);

// ─── Ordenação (adendo 0099 §4.3) ───────────────────────────────────────────
// Espelham EXATAMENTE a whitelist de `order_by` do backend; valor fora dela é
// 422, não no-op (navegacao.md §1.10). Default das duas listagens: `id desc`.
//
// ⚠ `status` ordena pela ordem do ciclo de vida do ENUM (OPEN → PARTIAL →
// CLOSED → CANCELLED; DISPATCHED → RECEIVED → RECEIVED_WITH_DIVERGENCE →
// CANCELLED), não alfabeticamente — agrupa por estágio, que é o que a tela
// quer dizer quando alguém clica em "Status".
export const ORDER_SORTABLE_KEYS = new Set([
   'id',
   'created_at',
   'updated_at',
   'status',
   'closed_at',
]);

export const SHIPMENT_SORTABLE_KEYS = new Set([
   'id',
   'dispatched_at',
   'received_at',
   'status',
]);

export const DEFAULT_ORDER_BY = 'id';
export const DEFAULT_ORDER_DIR = 'desc';

// Limites de texto do contrato.
export const CANCEL_REASON_MIN = 8;
export const CANCEL_REASON_MAX = 200;
export const DIVERGENCE_REASON_MIN = 5;
export const SETTLEMENT_MIN = 3;

export const ITEMS_PER_PAGE = 20;
